import { createClient, type RealtimeChannel } from '@supabase/supabase-js';
import { SUPABASE_KEY, SUPABASE_URL } from './api';

const client = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
});

type Listener = (connected: boolean, source: 'eb' | 'scorecard' | 'announcements' | 'connection') => void;
const listeners = new Set<Listener>();
let ebChannel: RealtimeChannel | null = null;
let scoreChannel: RealtimeChannel | null = null;
let announcementChannel: RealtimeChannel | null = null;
let currentCommittee = '';
type Source = 'eb' | 'scorecard' | 'announcements';
const timers = new Map<Source, ReturnType<typeof setTimeout>>();
const channelStates = new Map<Source, boolean>();
const subscribedBefore = new Set<Source>();

const notify = (connected: boolean, source: Source) => {
  if (timers.has(source)) return;
  timers.set(source, setTimeout(() => { timers.delete(source); listeners.forEach((listener) => listener(connected, source)); }, 120));
};

function connection(source: Source, status: string) {
  const online = status === 'SUBSCRIBED'; const previous = channelStates.get(source);
  channelStates.set(source, online);
  if (online && previous === false && subscribedBefore.has(source)) notify(true, source);
  if (online) subscribedBefore.add(source);
  const connected = channelStates.size === 3 && [...channelStates.values()].every(Boolean);
  listeners.forEach((item) => item(connected, 'connection'));
}

export function subscribeRealtime(committee: string, listener: Listener) {
  listeners.add(listener);
  if (!ebChannel) {
    ebChannel = client.channel(`desktop-eb-${committee}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'eb_committee_revision', filter: `committee_id=eq.${committee}` }, () => notify(true, 'eb'))
      .subscribe((status) => connection('eb', status));
  }
  if (!announcementChannel) {
    announcementChannel = client.channel('desktop-announcements')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'announcements_revision' }, () => notify(true, 'announcements'))
      .subscribe((status) => connection('announcements', status));
  }
  if (committee && currentCommittee !== committee) {
    if (scoreChannel) void client.removeChannel(scoreChannel);
    currentCommittee = committee;
    scoreChannel = client.channel(`desktop-scorecard-${committee}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'scorecard_revision', filter: `committee_id=eq.${committee}` }, () => notify(true, 'scorecard'))
      .subscribe((status) => connection('scorecard', status));
  }
  if (channelStates.size === 3) listener([...channelStates.values()].every(Boolean), 'connection');
  return () => {
    listeners.delete(listener);
    if (!listeners.size) {
      if (ebChannel) void client.removeChannel(ebChannel);
      if (scoreChannel) void client.removeChannel(scoreChannel);
      if (announcementChannel) void client.removeChannel(announcementChannel);
      ebChannel = null; scoreChannel = null; announcementChannel = null; currentCommittee = '';
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear(); channelStates.clear(); subscribedBefore.clear();
    }
  };
}
